'use client'
import { User } from '@/payload-types'
import { I18nClient } from '@payloadcms/translations'
import {
  Button,
  toast,
  useAllFormFields,
  useAuth,
  useConfig,
  useDocumentInfo,
  useListDrawer,
  useTranslation,
} from '@payloadcms/ui'
import { useRouter } from 'next/navigation'
import {
  ClientCollectionConfig,
  CollectionSlug,
  FilterOptionsResult,
  JoinFieldClientProps,
} from 'payload'
import React from 'react'
import { connectExistingDocuments } from './actions'
import './index.scss'

export type SelectExistingButtonProps = {
  baseClass: string
  className?: string
  collections: ClientCollectionConfig[]
  filterOptions?: FilterOptionsResult
  i18n: I18nClient
  label?: string
  onSelectExisting: (
    docs: Array<{ doc?: unknown; collectionSlug?: string; id?: string | number }>,
    closeDrawer: () => void,
  ) => void
  relationTo: string | string[]
  buttonStyle?:
    | 'error'
    | 'icon-label'
    | 'none'
    | 'pill'
    | 'primary'
    | 'secondary'
    | 'subtle'
    | 'tab'
    | 'transparent'
  icon?: React.ReactNode
}

type CustomClientProps = {
  roleNeeded?: string
}

const SelectExistingButton: React.FC<SelectExistingButtonProps> = ({
  baseClass,
  className,
  collections,
  filterOptions,
  i18n,
  label,
  onSelectExisting,
  relationTo,
  buttonStyle,
  icon,
}) => {
  const isPolymorphic = Array.isArray(relationTo)
  const [ListDrawer, , { openDrawer: openListDrawer, closeDrawer }] = useListDrawer({
    collectionSlugs: isPolymorphic
      ? (relationTo as CollectionSlug[])
      : ([relationTo] as CollectionSlug[]),
    filterOptions,
  })

  return (
    <div className={`${baseClass}__select-existing-wrapper`}>
      <Button buttonStyle={buttonStyle} className={className} icon={icon} onClick={openListDrawer}>
        {label ||
          i18n.t('fields:chooseFromExisting', {
            label: isPolymorphic
              ? i18n.t('general:documents')
              : collections.find((each) => each.slug === relationTo)?.labels?.plural || 'Items',
          })}
      </Button>
      <ListDrawer
        onSelect={({ doc, collectionSlug }) => {
          onSelectExisting([{ doc, collectionSlug }], closeDrawer)
        }}
        onBulkSelect={(selected) => {
          const selectedDocs = []
          for (const [id, isSelected] of selected) {
            if (isSelected) {
              selectedDocs.push({ id })
            }
          }
          onSelectExisting(selectedDocs, closeDrawer)
        }}
        enableRowSelections={true}
        allowCreate={false}
      />
    </div>
  )
}

export const SelectExistingBeforeInput: React.FC<JoinFieldClientProps & CustomClientProps> = ({
  field,
  roleNeeded,
}) => {
  const { i18n } = useTranslation()
  const configHook = useConfig()
  const documentInfo = useDocumentInfo()
  const { user } = useAuth<User>()
  const router = useRouter()
  const [_, dispatchFields] = useAllFormFields()
  const currentDocumentId = documentInfo.id

  // Check if we're in the correct context
  if (!configHook || !configHook.config) {
    console.warn(
      'SelectExistingBeforeInput: useConfig returned undefined, component not in correct context',
    )
    return <div>Component not in correct context</div>
  }

  const { config } = configHook

  const handleSelectExisting = async (
    docs: Array<{ doc?: unknown; collectionSlug?: string; id?: string | number }>,
    closeDrawer: () => void,
  ) => {
    console.log('Connecting existing documents:', docs)
    if (docs.length === 0) {
      console.warn('No documents selected to connect')
      return
    }

    if (!currentDocumentId) {
      console.error('No document ID found in current context')
      return
    }

    // Handle collection field (could be string or array)
    const collectionName = Array.isArray(field.collection) ? field.collection[0] : field.collection

    // Get collection labels for user-friendly messages
    const targetCollection = config.collections.find((c) => c.slug === collectionName)
    const collectionLabel = targetCollection?.labels?.plural || collectionName

    try {
      // Extract document IDs
      const documentIds = docs.map((doc) => doc.id).filter(Boolean)

      // Create form data for server action
      const formData = new FormData()
      formData.append('collection', collectionName)
      formData.append('fieldOn', field.on)
      formData.append('currentDocumentId', currentDocumentId.toString())
      formData.append('currentCollection', documentInfo.collectionSlug || 'unknown')
      formData.append('documentIds', JSON.stringify(documentIds))

      // Call server action
      const result = await connectExistingDocuments(formData)

      console.log('Server action result:', result)

      toast.success(`Successfully connected ${collectionLabel}`)

      // Close the drawer
      closeDrawer()

      // Give the server action time to complete and revalidatePath to take effect
      setTimeout(() => {
        console.log('Refreshing page to show updated join field data')
        router.refresh()
      }, 1000)
    } catch (error) {
      console.error(`Error connecting ${collectionName}:`, error)

      toast.error(`Failed to connect ${collectionLabel}`)
    }
  }

  if (!currentDocumentId) return null
  if (roleNeeded && !roleNeeded.includes(user?.role || 'student')) {
    console.debug('User does not have the required role')
    return null
  }

  if (field.type !== 'join') {
    // Only show the button if the field is a join field
    console.log('Field is not a join field, returning null. Field type:', field.type)
    return null
  }

  // Get collection labels for the button
  const targetCollection = config.collections.find((c) => c.slug === field.collection)
  const collectionLabel = targetCollection?.labels?.plural || field.collection

  return (
    <div>
      <SelectExistingButton
        baseClass="join-field"
        collections={config.collections}
        i18n={i18n}
        onSelectExisting={handleSelectExisting}
        relationTo={field.collection}
        label={`Connect Existing ${collectionLabel}`}
        buttonStyle="pill"
      />
    </div>
  )
}
